import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import Btn from '../../../../UI/Btn'
import CompanyProgress from '../../../../UI/CompanyProgress'
import FilePreview from '../../../../UI/FilePreview'
import Loading from '../../../../UI/Loading'
import MiniHeader from '../../../../UI/MiniHeader'

import http from '../../../../shared/http'
import { TApi, TRoutes } from '../../../../shared/const'
import { askConfirm, askReason } from '../../../../shared/confirmDialog'
import { JMC_STATUS } from '../../../../shared/miniCompanies'
import {
	DetailsList,
	SchoolDetails,
	DownloadsList,
	LogList,
	ParticipantsReadList,
	StatusPill,
	TeacherList,
	downloadPdf,
	formatDay
} from '../../common/MiniCompany/parts'

import styles from '../../common/MiniCompany/page.module.scss'

/**
 * Eine Junior Mini Company aus Sicht der Verwaltung (Anforderung #92):
 * Angaben, Kinder, Einverständniserklärungen ansehen, dann freigeben oder mit
 * Begründung zurückgeben. Ändern kann die Verwaltung nichts — das machen die
 * Lehrkräfte. Landesadmins öffnen nur ihr Bundesland (sonst 404 vom Server).
 */
export default function AdminMiniCompany() {
	const { t } = useTranslation()
	const history = useHistory()
	const { id } = useParams()
	const [jmc, setJmc] = useState(null)
	const [fehler, setFehler] = useState('')
	const [busy, setBusy] = useState('')
	const [vorschau, setVorschau] = useState(false)

	useEffect(() => {
		http.get(TApi.ADMIN_MINI_COMPANY(id), { disableToast: true })
			.then(({ data }) => setJmc(data))
			.catch(() => setFehler(t('The Junior Mini Company could not be loaded.')))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id])

	if (fehler) return <div className={styles.root}><p className={styles.notice}>{fehler}</p></div>
	if (!jmc) return <Loading />

	const eingereicht = jmc.status === JMC_STATUS.SUBMITTED

	const freigeben = async () => {
		const ok = await askConfirm({
			title: t('Approve Junior Mini Company?'),
			text: t('The teachers are informed by e-mail. Afterwards the details and the list of children are fixed.'),
			confirmLabel: t('Approve')
		})
		if (!ok) return
		setBusy('approve')
		http.put(TApi.ADMIN_MINI_COMPANY_APPROVE(id), {})
			.then(({ data }) => {
				setJmc(data)
				toast.success(t('Approved.'))
			})
			.finally(() => setBusy(''))
	}

	const zurueckgeben = async () => {
		const reason = await askReason({
			title: t('Return Junior Mini Company?'),
			text: t('The teachers receive your reason by e-mail and can change the details and submit again.'),
			confirmLabel: t('Return'),
			tone: 'danger'
		})
		if (!reason) return
		setBusy('reject')
		http.put(TApi.ADMIN_MINI_COMPANY_REJECT(id), { reason })
			.then(({ data }) => {
				setJmc(data)
				toast.success(t('Returned to the teachers.'))
			})
			.finally(() => setBusy(''))
	}

	return (
		<div className={styles.root}>
			<MiniHeader className={styles.miniHeader} title={jmc.name} bg="#f8faff">
				<div className={styles.headActions}>
					<StatusPill status={jmc.status} />
					<Btn theme="secondary" onClick={() => history.push(TRoutes.ADMIN_COMPANIES)}>
						{t('Back to the list')}
					</Btn>
					{eingereicht && (
						<>
							<Btn theme="danger" onClick={zurueckgeben} loading={busy === 'reject'}>
								{t('Return')}
							</Btn>
							<Btn onClick={freigeben} loading={busy === 'approve'}>
								{t('Approve')}
							</Btn>
						</>
					)}
				</div>
			</MiniHeader>

			<CompanyProgress miniCompany={jmc} compact className={styles.progress} />

			{jmc.status === JMC_STATUS.REJECTED && jmc.rejectionReason && (
				<div className={`${styles.banner} ${styles.bannerRed}`}>
					<strong>{t('Returned by the state administration:')}</strong> {jmc.rejectionReason}
				</div>
			)}

			<div className={styles.columns}>
				<div className={styles.main}>
					<section className={styles.card}>
						<h2 className={styles.cardTitle}>{t('1. Details')}</h2>
						<DetailsList miniCompany={jmc} />
					</section>

					<section className={styles.card}>
						<h2 className={styles.cardTitle}>{t('School')}</h2>
						<SchoolDetails school={jmc.school} state={jmc.state} schoolLink={jmc.school ? TRoutes.UPDATE_SCHOOL(jmc.school.id) : null} />
					</section>

					<section className={styles.card}>
						<h2 className={styles.cardTitle}>
							{t('2. Children')} <span className={styles.count}>{jmc.participants.length}</span>
						</h2>
						<ParticipantsReadList participants={jmc.participants} />
					</section>

					<section className={styles.card}>
						<h2 className={styles.cardTitle}>{t('3. Downloads')}</h2>
						<DownloadsList />
					</section>

					<section className={styles.card}>
						<h2 className={styles.cardTitle}>{t('4. Consent forms')}</h2>
						{jmc.consent ? (
							<p className={styles.file}>
								<button type="button" className={styles.fileLink} onClick={() => setVorschau(true)}>
									{jmc.consent.originalName}
								</button>
								<span>{t('Uploaded on {{date}}', { date: formatDay(jmc.consent.uploadedAt) })}</span>
							</p>
						) : (
							<p className={styles.muted}>{t('No file uploaded yet.')}</p>
						)}
					</section>

					<section className={styles.card}>
						<h2 className={styles.cardTitle}>{t('JMC certificates')}</h2>
						{jmc.certificatesGeneratedAt ? (
							<>
								<p className={styles.hint}>{t('Generated on {{date}}.', { date: formatDay(jmc.certificatesGeneratedAt) })}</p>
								<Btn
									theme="secondary"
									onClick={() =>
										downloadPdf(TApi.ADMIN_MINI_COMPANY_CERTIFICATES(id), `Zertifikate ${jmc.name}.pdf`)
									}
								>
									{t('Download certificates')}
								</Btn>
							</>
						) : (
							<p className={styles.muted}>{t('No entries yet.')}</p>
						)}
					</section>
				</div>

				<aside className={styles.side}>
					<section className={styles.card}>
						<h2 className={styles.cardTitle}>{t('Teachers')}</h2>
						<TeacherList teachers={jmc.teachers || []} />
					</section>
					<section className={styles.card}>
						<h2 className={styles.cardTitle}>{t('History')}</h2>
						<LogList items={jmc.log} />
					</section>
				</aside>
			</div>

			{vorschau && jmc.consent && (
				<FilePreview
					items={[{ name: jmc.consent.originalName, url: TApi.ADMIN_MINI_COMPANY_CONSENT(id) }]}
					index={0}
					onIndexChange={() => {}}
					onClose={() => setVorschau(false)}
					onDownload={() => downloadPdf(TApi.ADMIN_MINI_COMPANY_CONSENT(id), jmc.consent.originalName)}
				/>
			)}
		</div>
	)
}
